import { useState, useCallback, useMemo } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { Button, Card, CardContent, Input, Label } from '@pantry/ui';
import { cn } from '@pantry/ui';
import { logger } from '../../lib/logger';
import {
  useUpdateItemInstance,
  WASTE_REASONS,
  WASTE_REASON_LABELS,
  CONSUME_REASONS,
  CONSUME_REASON_LABELS,
  parseQuantityExpression,
  convertQuantity,
  getUnitCategory,
  UNIT_DISPLAY_LABELS,
  type WasteReason,
  type ConsumeReason,
  type ItemInstance,
  type ItemUnit,
} from '@pantry/api-client';
import {
  XMarkIcon,
  MinusIcon,
  PlusIcon,
  CheckIcon,
  TrashIcon,
  ArrowLeftIcon,
  FireIcon,
  UsersIcon,
  GiftIcon,
  SparklesIcon,
  BeakerIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline';
import { UnitSelector } from './unit-selector';
import { UnitConversionTooltip } from './unit-conversion-tooltip';

type ActionType = 'consume' | 'waste';

interface QuantityActionDialogProps {
  householdId: string;
  itemId: string;
  instances: ItemInstance[];
  /** The item's stored unit */
  unit: ItemUnit;
  action: ActionType;
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Get icon for consume reason
 */
function getConsumeReasonIcon(reason: ConsumeReason) {
  switch (reason) {
    case 'cooked':
      return FireIcon;
    case 'eaten_raw':
      return SparklesIcon;
    case 'shared':
      return UsersIcon;
    case 'donated':
      return GiftIcon;
    case 'other':
    default:
      return BeakerIcon;
  }
}

/**
 * Get icon for waste reason
 */
function getWasteReasonIcon(reason: WasteReason) {
  switch (reason) {
    case 'expired':
      return ClockIcon;
    case 'spoiled':
      return ExclamationTriangleIcon;
    case 'damaged':
      return NoSymbolIcon;
    case 'other':
    default:
      return QuestionMarkCircleIcon;
  }
}

/**
 * Dialog for selecting quantity when consuming or wasting multiple item instances.
 * Now works with instance quantities - user selects total items to consume/waste
 * and the system processes instances in order (sorted by expiry date).
 */
export function QuantityActionDialog({
  householdId,
  itemId,
  instances,
  unit,
  action,
  onClose,
  onSuccess,
}: QuantityActionDialogProps) {
  const { t } = useLingui();
  // Total quantity across all instances (in stored unit)
  const totalQuantityInStoredUnit = instances.reduce((sum, i) => sum + i.quantity, 0);
  const [quantity, setQuantity] = useState(1);
  const [step, setStep] = useState<'quantity' | 'reason'>('quantity');
  const [selectedWasteReason, setSelectedWasteReason] = useState<WasteReason | null>(null);
  const [selectedConsumeReason, setSelectedConsumeReason] = useState<ConsumeReason | null>(null);
  const [statusNotes, setStatusNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [processingIndex, setProcessingIndex] = useState<number | null>(null);

  // Unit switching state - user can input in any related unit
  const [inputUnit, setInputUnit] = useState<ItemUnit>(unit);

  // Manual input state
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(String(quantity));

  // Check if unit switching is available (not for discrete items)
  const canSwitchUnits = getUnitCategory(unit) !== 'discrete';

  // Convert total quantity to input unit for display
  const totalQuantity = useMemo(() => {
    if (inputUnit === unit) return totalQuantityInStoredUnit;
    const converted = convertQuantity(totalQuantityInStoredUnit, unit, inputUnit);
    return converted !== null ? Math.round(converted * 1000) / 1000 : totalQuantityInStoredUnit;
  }, [totalQuantityInStoredUnit, unit, inputUnit]);

  // Handle unit change - convert quantity to new unit
  const handleUnitChange = useCallback(
    (newUnit: ItemUnit) => {
      if (newUnit === inputUnit) return;
      const converted = convertQuantity(quantity, inputUnit, newUnit);
      if (converted !== null) {
        const rounded = Math.round(converted * 1000) / 1000;
        setQuantity(rounded);
        setInputValue(String(rounded));
      }
      setInputUnit(newUnit);
    },
    [quantity, inputUnit]
  );

  const updateInstance = useUpdateItemInstance();

  const handleQuantityChange = (delta: number) => {
    setQuantity((prev) => Math.max(1, Math.min(totalQuantity, prev + delta)));
  };

  // Handle manual input blur - parse expression and validate
  const handleInputBlur = useCallback(() => {
    setIsEditing(false);

    // Try to parse as expression (supports "2*250", "500+250", etc.)
    const parsed = parseQuantityExpression(inputValue);

    if (parsed !== null) {
      // Clamp to valid range
      const clamped = Math.max(1, Math.min(totalQuantity, parsed));
      // Round to 3 decimal places
      const rounded = Math.round(clamped * 1000) / 1000;
      setQuantity(rounded);
      setInputValue(String(rounded));
    } else {
      // Invalid input - revert to current quantity
      setInputValue(String(quantity));
    }
  }, [inputValue, totalQuantity, quantity]);

  // Handle clicking on quantity display to enter edit mode
  const handleQuantityClick = useCallback(() => {
    // Check processingIndex directly since isProcessing is derived later
    if (processingIndex === null) {
      setIsEditing(true);
      setInputValue(String(quantity));
    }
  }, [quantity, processingIndex]);

  // Handle Enter key to submit input
  const handleInputKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleInputBlur();
      } else if (e.key === 'Escape') {
        setIsEditing(false);
        setInputValue(String(quantity));
      }
    },
    [handleInputBlur, quantity]
  );

  const handleProceed = () => {
    // Both consume and waste actions now require a reason step
    setStep('reason');
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault();
    setError(null);

    if (action === 'waste' && !selectedWasteReason) {
      setError(t`Please select a reason for wasting these items`);
      return;
    }

    if (action === 'consume' && !selectedConsumeReason) {
      setError(t`Please select how you used these items`);
      return;
    }

    try {
      // Convert input quantity to stored unit for API
      let quantityInStoredUnit = quantity;
      if (inputUnit !== unit) {
        const converted = convertQuantity(quantity, inputUnit, unit);
        if (converted === null) {
          setError(t`Failed to convert quantity`);
          return;
        }
        quantityInStoredUnit = Math.round(converted * 1000) / 1000;
      }

      // Process instances in order, handling partial consumption
      // Instances should already be sorted by expiry date (earliest first)
      let remainingToConsume = quantityInStoredUnit;
      let processedCount = 0;

      for (const instance of instances) {
        if (remainingToConsume <= 0) break;

        setProcessingIndex(processedCount);
        processedCount++;

        // Determine how much to consume from this instance
        const quantityFromThisInstance = Math.min(remainingToConsume, instance.quantity);
        remainingToConsume -= quantityFromThisInstance;

        await updateInstance.mutateAsync({
          householdId,
          itemId,
          instanceId: instance.id,
          data: {
            status: action === 'consume' ? 'consumed' : 'wasted',
            ...(action === 'waste' && selectedWasteReason
              ? {
                  wasteReason: selectedWasteReason,
                  statusNotes: statusNotes.trim() || undefined,
                }
              : {}),
            ...(action === 'consume' && selectedConsumeReason
              ? {
                  consumeReason: selectedConsumeReason,
                  statusNotes: statusNotes.trim() || undefined,
                }
              : {}),
            // Only include quantityToConsume if partial consumption
            ...(quantityFromThisInstance < instance.quantity
              ? { quantityToConsume: quantityFromThisInstance }
              : {}),
          },
        });
      }

      setProcessingIndex(null);
      onSuccess?.();
      onClose();
    } catch (err) {
      logger.error(`Failed to mark as ${action}`, err, { householdId, itemId, action });
      setError(err instanceof Error ? err.message : t`Failed to update items`);
      setProcessingIndex(null);
    }
  };

  const isProcessing = processingIndex !== null;
  const isConsume = action === 'consume';
  const accentColor = isConsume ? 'fresh' : 'expired';

  // Quantity selection step
  if (step === 'quantity') {
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm animate-in fade-in duration-200"
        onClick={(e) => e.target === e.currentTarget && !isProcessing && onClose()}
      >
        <Card className="w-full max-w-sm animate-in slide-in-from-bottom-4 duration-300 shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 pb-4">
            <div className="flex items-center gap-3">
              <div
                className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-xl',
                  isConsume ? 'bg-fresh/10' : 'bg-expired/10'
                )}
              >
                {isConsume ? (
                  <CheckIcon className="h-5 w-5 text-fresh" />
                ) : (
                  <TrashIcon className="h-5 w-5 text-expired" />
                )}
              </div>
              <div>
                <h2 className="font-heading font-semibold text-lg text-foreground">
                  {isConsume ? <Trans>Mark as Used</Trans> : <Trans>Mark as Wasted</Trans>}
                </h2>
                <p className="text-sm text-muted-foreground">
                  <Plural
                    value={instances.length}
                    one="# batch available"
                    other="# batches available"
                  />
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 rounded-full hover:bg-muted"
              onClick={onClose}
              disabled={isProcessing}
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>

          <CardContent className="pt-0">
            <div className="space-y-5">
              {error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in slide-in-from-top-2">
                  {error}
                </div>
              )}

              {/* Quantity selector */}
              <div className="space-y-4 p-4 rounded-xl bg-muted/30 border border-border/50">
                <Label className="text-center block text-sm font-medium">
                  {isConsume ? <Trans>How much to use?</Trans> : <Trans>How much to waste?</Trans>}
                </Label>
                <div className="flex items-center justify-center gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(-1)}
                    disabled={quantity <= 0.001 || isProcessing}
                    className="h-12 w-12 rounded-xl"
                  >
                    <MinusIcon className="h-5 w-5" />
                  </Button>
                  <div className="flex flex-col items-center min-w-[120px]">
                    <div className="flex items-center gap-1">
                      {isEditing ? (
                        <Input
                          type="text"
                          inputMode="decimal"
                          value={inputValue}
                          onChange={(e) => setInputValue(e.target.value)}
                          onBlur={handleInputBlur}
                          onKeyDown={handleInputKeyDown}
                          autoFocus
                          className="w-20 h-12 text-center text-2xl font-bold rounded-xl"
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={handleQuantityClick}
                          disabled={isProcessing}
                          className={cn(
                            'text-4xl font-bold transition-colors cursor-text disabled:cursor-not-allowed disabled:opacity-50',
                            isConsume ? 'hover:text-fresh' : 'hover:text-expired'
                          )}
                          title={t`Click to edit`}
                        >
                          {quantity}
                        </button>
                      )}
                      {canSwitchUnits ? (
                        <UnitSelector
                          value={inputUnit}
                          onChange={handleUnitChange}
                          baseUnit={unit}
                          disabled={isProcessing}
                        />
                      ) : (
                        <span className="text-sm text-muted-foreground">
                          {UNIT_DISPLAY_LABELS[unit]}
                        </span>
                      )}
                      <UnitConversionTooltip unit={inputUnit} />
                    </div>
                    <span className="text-sm text-muted-foreground mt-1">
                      <Trans>
                        of {totalQuantity} {UNIT_DISPLAY_LABELS[inputUnit]} available
                      </Trans>
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => handleQuantityChange(1)}
                    disabled={quantity >= totalQuantity || isProcessing}
                    className="h-12 w-12 rounded-xl"
                  >
                    <PlusIcon className="h-5 w-5" />
                  </Button>
                </div>
              </div>

              {/* Quick selection buttons */}
              {totalQuantity > 2 && (
                <div className="flex justify-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(1)}
                    disabled={isProcessing}
                    className={cn(
                      'rounded-lg',
                      quantity === 1 &&
                        `border-${accentColor} bg-${accentColor}/10 text-${accentColor}`
                    )}
                  >
                    1
                  </Button>
                  {totalQuantity >= 5 && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setQuantity(Math.min(5, totalQuantity))}
                      disabled={isProcessing}
                      className={cn(
                        'rounded-lg',
                        quantity === Math.min(5, totalQuantity) &&
                          `border-${accentColor} bg-${accentColor}/10 text-${accentColor}`
                      )}
                    >
                      5
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setQuantity(totalQuantity)}
                    disabled={isProcessing}
                    className={cn(
                      'rounded-lg',
                      quantity === totalQuantity &&
                        `border-${accentColor} bg-${accentColor}/10 text-${accentColor}`
                    )}
                  >
                    <Trans>All ({totalQuantity})</Trans>
                  </Button>
                </div>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="rounded-xl"
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  type="button"
                  onClick={handleProceed}
                  disabled={isProcessing}
                  className={cn(
                    'rounded-xl min-w-[100px]',
                    isConsume
                      ? 'bg-fresh hover:bg-fresh/90 text-white'
                      : 'bg-expired hover:bg-expired/90 text-white'
                  )}
                >
                  {isProcessing ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                    </div>
                  ) : (
                    <Trans>Next</Trans>
                  )}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reason step (for both consume and waste actions)
  const reasons = isConsume ? CONSUME_REASONS : WASTE_REASONS;
  const reasonLabels = isConsume ? CONSUME_REASON_LABELS : WASTE_REASON_LABELS;
  const selectedReason = isConsume ? selectedConsumeReason : selectedWasteReason;
  const setSelectedReason = isConsume ? setSelectedConsumeReason : setSelectedWasteReason;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/20 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={(e) => e.target === e.currentTarget && !isProcessing && onClose()}
    >
      <Card className="w-full max-w-md animate-in slide-in-from-bottom-4 duration-300 shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-3">
            <div
              className={cn(
                'flex h-10 w-10 items-center justify-center rounded-xl',
                isConsume ? 'bg-fresh/10' : 'bg-expired/10'
              )}
            >
              {isConsume ? (
                <CheckIcon className="h-5 w-5 text-fresh" />
              ) : (
                <TrashIcon className="h-5 w-5 text-expired" />
              )}
            </div>
            <div>
              <h2 className="font-heading font-semibold text-lg text-foreground">
                {isConsume ? <Trans>How was it used?</Trans> : <Trans>What happened?</Trans>}
              </h2>
              <p className="text-sm text-muted-foreground">
                <Trans>
                  {quantity} {UNIT_DISPLAY_LABELS[inputUnit]} selected
                </Trans>
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 rounded-full hover:bg-muted"
            onClick={onClose}
            disabled={isProcessing}
          >
            <XMarkIcon className="h-4 w-4" />
          </Button>
        </div>

        <CardContent className="pt-0">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in slide-in-from-top-2">
                {error}
              </div>
            )}

            {/* Reason selection */}
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                <Trans>Select a reason</Trans>
              </Label>
              <div className="grid gap-2">
                {reasons.map((reason, index) => {
                  const Icon = isConsume
                    ? getConsumeReasonIcon(reason as ConsumeReason)
                    : getWasteReasonIcon(reason as WasteReason);
                  const isSelected = selectedReason === reason;
                  return (
                    <button
                      key={reason}
                      type="button"
                      onClick={() => setSelectedReason(reason as WasteReason & ConsumeReason)}
                      disabled={isProcessing}
                      className={cn(
                        'flex items-center w-full px-4 py-3 rounded-xl border text-left transition-all duration-200 animate-in fade-in',
                        isSelected
                          ? isConsume
                            ? 'border-fresh bg-fresh/5 shadow-sm'
                            : 'border-expired bg-expired/5 shadow-sm'
                          : 'border-border/50 bg-card hover:bg-muted/50 hover:border-border',
                        isProcessing && 'opacity-50 cursor-not-allowed'
                      )}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      <div
                        className={cn(
                          'flex h-9 w-9 items-center justify-center rounded-lg mr-3 transition-colors',
                          isSelected ? (isConsume ? 'bg-fresh/20' : 'bg-expired/20') : 'bg-muted'
                        )}
                      >
                        <Icon
                          className={cn(
                            'h-5 w-5 transition-colors',
                            isSelected
                              ? isConsume
                                ? 'text-fresh'
                                : 'text-expired'
                              : 'text-muted-foreground'
                          )}
                        />
                      </div>
                      <span
                        className={cn(
                          'flex-1 font-medium transition-colors',
                          isSelected ? 'text-foreground' : 'text-muted-foreground'
                        )}
                      >
                        {reasonLabels[reason as keyof typeof reasonLabels]}
                      </span>
                      {isSelected && (
                        <div
                          className={cn(
                            'h-6 w-6 rounded-full flex items-center justify-center animate-in zoom-in duration-200',
                            isConsume ? 'bg-fresh' : 'bg-expired'
                          )}
                        >
                          <CheckIcon className="h-4 w-4 text-white" strokeWidth={3} />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Optional notes */}
            <div className="space-y-2">
              <Label htmlFor="statusNotes" className="text-sm font-medium">
                <Trans>
                  Notes <span className="text-muted-foreground font-normal">(optional)</span>
                </Trans>
              </Label>
              <textarea
                id="statusNotes"
                className={cn(
                  'flex min-h-[80px] w-full rounded-xl border border-border/50 bg-muted/30 px-4 py-3 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 transition-all resize-none',
                  isConsume
                    ? 'focus-visible:ring-fresh/50 focus-visible:border-fresh/50'
                    : 'focus-visible:ring-expired/50 focus-visible:border-expired/50'
                )}
                placeholder={t`Any additional details...`}
                value={statusNotes}
                onChange={(e) => setStatusNotes(e.target.value)}
                maxLength={500}
                disabled={isProcessing}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-between gap-3 pt-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setStep('quantity')}
                disabled={isProcessing}
                className="rounded-xl"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                <Trans>Back</Trans>
              </Button>
              <div className="flex gap-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="rounded-xl"
                >
                  <Trans>Cancel</Trans>
                </Button>
                <Button
                  type="submit"
                  disabled={!selectedReason || isProcessing}
                  className={cn(
                    'rounded-xl min-w-[140px]',
                    isConsume
                      ? 'bg-fresh hover:bg-fresh/90 text-white'
                      : 'bg-expired hover:bg-expired/90 text-white'
                  )}
                >
                  {isProcessing ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                      <Trans>Processing...</Trans>
                    </div>
                  ) : isConsume ? (
                    <Trans>
                      Use {quantity} {UNIT_DISPLAY_LABELS[inputUnit]}
                    </Trans>
                  ) : (
                    <Trans>
                      Waste {quantity} {UNIT_DISPLAY_LABELS[inputUnit]}
                    </Trans>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
